import { formatCurrency } from "@/lib/formatting/currency";
import { keys } from "@/lib/i18n/portal";
import type { useTranslations } from "@/lib/i18n/intl";
import type { CouponView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";

type Translate = ReturnType<typeof useTranslations>;

export function isCouponExpired(coupon: CouponView): boolean {
  if (!coupon.expiresAt) return false;
  const at = new Date(coupon.expiresAt).getTime();
  return !Number.isNaN(at) && at < Date.now();
}

export function isCouponActive(coupon: CouponView): boolean {
  const exhausted =
    coupon.maxUses !== null && coupon.usesCount >= coupon.maxUses;
  return coupon.active && !exhausted && !isCouponExpired(coupon);
}

/**
 * Big value string: "50%" / "EGP 100" / "Free delivery" /
 * "Buy 2 get 1 free" / "Buy 2 get 1 at 50% off".
 */
export function couponValueText(
  coupon: CouponView,
  locale: Locale,
  t: Translate,
): string {
  if (coupon.kind === "FREE_DELIVERY") return t(keys.coupons.kindFreeDelivery);
  if (coupon.kind === "BUY_X_GET_Y") {
    const buy = coupon.buyQuantity ?? 0;
    const get = coupon.getQuantity ?? 0;
    const percent = coupon.getDiscountPercent ?? 0;
    return percent >= 100
      ? t(keys.coupons.valueBogoFree, { buy, get })
      : t(keys.coupons.valueBogoPercent, { buy, get, percent });
  }
  // CART_DISCOUNT + PRODUCT_DISCOUNT share the unit/value shape.
  if (coupon.discountUnit === "PERCENT") return `${coupon.discountValue ?? 0}%`;
  return formatCurrency(coupon.discountValue ?? 0, locale);
}

export type CouponBadge = { label: string; bg: string; fg: string };

/**
 * Colored type badge: Free shipping / Buy X get Y / Product / Percentage /
 * Fixed amount.
 */
export function couponTypeBadge(coupon: CouponView, t: Translate): CouponBadge {
  if (coupon.kind === "FREE_DELIVERY") {
    return {
      label: t(keys.coupons.typeFree),
      fg: "#7C3AED",
      bg: "color-mix(in oklab, #7C3AED 13%, transparent)",
    };
  }
  if (coupon.kind === "BUY_X_GET_Y") {
    return {
      label: t(keys.coupons.typeBogo),
      fg: "#0D9488",
      bg: "color-mix(in oklab, #0D9488 13%, transparent)",
    };
  }
  if (coupon.kind === "PRODUCT_DISCOUNT") {
    return {
      label: t(keys.coupons.typeProduct),
      fg: "#4F46E5",
      bg: "color-mix(in oklab, #4F46E5 13%, transparent)",
    };
  }
  if (coupon.discountUnit === "PERCENT") {
    return {
      label: t(keys.coupons.typePercent),
      fg: "#2563EB",
      bg: "color-mix(in oklab, #2563EB 13%, transparent)",
    };
  }
  return {
    label: t(keys.coupons.typeFixed),
    fg: "#B45309",
    bg: "color-mix(in oklab, #B45309 13%, transparent)",
  };
}

export type CouponStatusTone = {
  label: string;
  dot: string;
  bg: string;
  fg: string;
};

export function couponStatusTone(
  coupon: CouponView,
  t: Translate,
): CouponStatusTone {
  const exhausted =
    coupon.maxUses !== null && coupon.usesCount >= coupon.maxUses;
  const expired = isCouponExpired(coupon);
  if (coupon.active && !exhausted && !expired) {
    return {
      label: t(keys.coupons.statusActive),
      dot: "#16A34A",
      bg: "color-mix(in oklab, #16A34A 12%, transparent)",
      fg: "#15803D",
    };
  }
  if (expired) {
    return {
      label: t(keys.coupons.statusExpired),
      dot: "#D97706",
      bg: "color-mix(in oklab, #D97706 12%, transparent)",
      fg: "#B45309",
    };
  }
  if (exhausted) {
    return {
      label: t(keys.coupons.statusExhausted),
      dot: "#E5484D",
      bg: "color-mix(in oklab, #E5484D 12%, transparent)",
      fg: "var(--error-500)",
    };
  }
  return {
    label: t(keys.coupons.statusInactive),
    dot: "var(--text-disabled)",
    bg: "var(--surface-alt)",
    fg: "var(--text-muted)",
  };
}

/** Redemption progress: max label (∞ when unlimited) + percent + bar colour. */
export function couponUsage(coupon: CouponView, tone: CouponStatusTone) {
  const maxLabel = coupon.maxUses === null ? "∞" : String(coupon.maxUses);
  const pct =
    coupon.maxUses && coupon.maxUses > 0
      ? Math.min(100, Math.round((coupon.usesCount / coupon.maxUses) * 100))
      : 0;
  const barColor = tone.fg === "#15803D" ? "#16A34A" : "var(--text-disabled)";
  return { maxLabel, pct, barColor };
}

export function formatCouponDate(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

/** Expiry date + time, e.g. "10 Jul 2026, 11:59 PM". Empty when no expiry. */
export function formatCouponExpiry(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/** "Min. order EGP 200" — null when the coupon has no minimum. */
export function couponMinOrderText(
  coupon: CouponView,
  locale: Locale,
  t: Translate,
): string | null {
  if (coupon.minOrderAmount == null) return null;
  return t(keys.coupons.minOrderValue, {
    amount: formatCurrency(coupon.minOrderAmount, locale),
  });
}

/** Card/table validity line: "Valid till {date}" / "Expired {date}" / "No expiry". */
export function couponValidityText(
  coupon: CouponView,
  locale: Locale,
  t: Translate,
): string {
  if (!coupon.expiresAt) return t(keys.coupons.noExpiry);
  const dateStr = formatCouponDate(coupon.expiresAt, locale);
  return isCouponExpired(coupon)
    ? t(keys.coupons.expiredOn, { date: dateStr })
    : t(keys.coupons.validTill, { date: dateStr });
}
